import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Shield, Users as UsersIcon, Plus, Edit3, Trash2, X, Save, Search,
    Check, ChevronDown, ChevronUp, Eye, EyeOff, DollarSign, Download,
    Loader2, AlertCircle, Lock, Unlock, Copy, RefreshCw,
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    getFirestore, collection, getDocs, doc, getDoc, setDoc, deleteDoc,
    onSnapshot, query, where, serverTimestamp, updateDoc, addDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const ALL = '*';

const ALL_DIMENSIONS = [
    { key: 'companies', label: 'Companies', description: 'Which companies (IMG, BWI, etc.)' },
    { key: 'divisions', label: 'Divisions', description: 'Which divisions (InMocean, Anne Cole, etc.)' },
    { key: 'customers', label: 'Customers', description: 'Which customer accounts' },
    { key: 'salespersonCodes', label: 'Salesperson Codes', description: 'Assigned salesperson codes' },
    { key: 'productionManagerCodes', label: 'Production Manager Codes', description: 'Assigned PM codes (po_mgr)' },
    { key: 'warehouses', label: 'Warehouses', description: 'Which warehouses / locations' },
];

const CAPABILITY_FLAGS = [
    { key: 'canSeeCosts', label: 'Can see costs', description: 'Show cost $, GP $ and margin amounts' },
    { key: 'canSeeMargins', label: 'Can see margin %', description: 'Show GP % (without dollar amounts)' },
    { key: 'canExport', label: 'Can export', description: 'Download data as CSV/XLSX' },
];

const PAGE_GROUPS = [
    { label: 'Core', pages: [
        ['inmocean/home', 'Home'],
        ['inmocean/alerts', 'Alerts'],
        ['inmocean/orders', 'Orders'],
        ['inmocean/production', 'Production'],
        ['inmocean/finance', 'Finance'],
        ['inmocean/inventory', 'Inventory'],
    ]},
    { label: 'Imports', pages: [
        ['inmocean/imports', 'Imports (nav)'],
        ['imports/actual-cost', 'Actual Cost'],
        ['imports/actual-cost/cost-analysis', '  ↳ Cost Analysis'],
        ['imports/actual-cost/missing-data', '  ↳ Missing Data'],
        ['imports/actual-cost/data-quality', '  ↳ Data Quality'],
        ['imports/actual-cost/settings', '  ↳ Settings'],
        ['imports/first-sale', 'First Sale'],
        ['imports/first-sale/review', '  ↳ Review'],
        ['imports/first-sale/errors', '  ↳ Errors'],
        ['imports/first-sale/settings', '  ↳ Settings'],
        ['imports/production-tracking', 'Production Tracking'],
        ['imports/production-tracking-beta', 'Production Tracking (Beta)'],
        ['imports/pipeline-tracker', 'Pipeline Tracker'],
        ['imports/tariff-tracker', 'Tariff Tracker'],
    ]},
    { label: 'Logistics', pages: [
        ['inmocean/logistics', 'Logistics'],
        ['inmocean/routing-beta', 'Routing (Beta)'],
    ]},
    { label: 'Data & Reports', pages: [
        ['inmocean/data', 'Data (nav)'],
        ['data/powerbi', 'Power BI'],
        ['data/powerbi-beta', 'Power BI (Beta)'],
        ['data/importdata', 'Import Data'],
        ['data/target', 'Target Balance Sheet'],
        ['data/analytics', 'Analytics'],
        ['data/table-status', 'Table Status'],
        ['data/data-quality', 'Data Quality'],
        ['data/first-sale-errors', 'First Sale Errors'],
        ['data/test', 'Test'],
    ]},
    { label: 'Admin & AI', pages: [
        ['inmocean/admin', 'Admin'],
        ['agents', 'Agents (Yulie)'],
        ['ai-chat', 'AI Chat'],
    ]},
];

// ------------------------------------------------------------------------
// Service: thin wrapper over Firestore + callables
// ------------------------------------------------------------------------
class RolesUsersService {
    // Lazy getters — don't call getFirestore()/getFunctions() until Firebase has initialized
    get db() { return getFirestore(); }
    get fns() { return getFunctions(); }

    rolesRef(tenantId) { return collection(this.db, `tenants/${tenantId}/roles`); }

    async loadRoles(tenantId) {
        const snap = await getDocs(this.rolesRef(tenantId));
        return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    /**
     * Load all users in a tenant via the listUsers callable.
     *
     * CHA-97 / PR-C: direct userProfiles collection query is blocked
     * at the rules layer (read-self only). The listUsers callable
     * composes roleAssignments (canonical role source) + Firebase Auth
     * (canonical identity source) into a single shape.
     *
     * Returned user shape (per CHA-97 spec):
     *   { uid, email, displayName, disabled, emailVerified,
     *     roleId, tenantId, assignedBy, assignedAt, _orphan? }
     */
    async loadUsers(tenantId) {
        const fn = httpsCallable(this.fns, 'listUsers');
        const result = await fn({ tenantId });
        return result.data?.users || [];
    }

    async saveRole(tenantId, roleId, data) {
        const ref = doc(this.db, `tenants/${tenantId}/roles/${roleId}`);
        await setDoc(ref, { ...data, updatedAt: new Date().toISOString() }, { merge: true });
    }

    async deleteRole(tenantId, roleId) {
        await deleteDoc(doc(this.db, `tenants/${tenantId}/roles/${roleId}`));
    }

    /**
     * Update a user's role.
     *
     * CHA-97 / PR-C: signature added tenantId (required by the
     * assignUserRole callable). Non-role fields ignored with warning;
     * status changes go through setUserStatus instead.
     */
    async updateUser(uid, tenantId, updates) {
        const { roleId, ...nonRoleFields } = updates || {};
        if (Object.keys(nonRoleFields).length > 0) {
            console.warn('Non-role fields ignored in updateUser (CHA-117):', Object.keys(nonRoleFields));
        }
        if (!roleId) {
            throw new Error('updateUser requires roleId in updates');
        }
        const fn = httpsCallable(this.fns, 'assignUserRole');
        const result = await fn({ targetUid: uid, tenantId, roleId });
        return result.data;
    }

    /**
     * Set a user's status (active | inactive).
     *
     * CHA-97 / PR-C: routes through the setUserStatus callable. The
     * legacy 'pending' state is no longer valid here — pending users
     * live in `user_invitations` (separate UI surface).
     */
    async setUserStatus(uid, tenantId, status) {
        if (status !== 'active' && status !== 'inactive') {
            throw new Error(`setUserStatus requires status='active' or 'inactive'; got: ${status}`);
        }
        const fn = httpsCallable(this.fns, 'setUserStatus');
        const result = await fn({ targetUid: uid, tenantId, status });
        return result.data;
    }

    /**
     * Invite a new user. Creates a user_invitations doc, which auto-
     * triggers the sendInvitationEmail Firestore function.
     *
     * CHA-97 / PR-C (Decision α): the synthetic `pending_<ts>` userProfile
     * stub is no longer written. The user_invitations doc IS the durable
     * pending-invitation record; the real userProfiles + roleAssignments
     * pair gets created when the invitee accepts via the
     * acceptUserInvitation flow (UserManagementService.acceptUserInvitation
     * → saveUserProfile → assignUserRole callable).
     *
     * The `pendingUid` field is also dropped from the invitation doc —
     * it would have been a dangling reference to a userProfile that no
     * longer exists.
     */
    async inviteUser(tenantId, { email, name, roleId }) {
        const emailLower = String(email).trim().toLowerCase();
        if (!emailLower) throw new Error('Email is required');
        if (!roleId) throw new Error('Role is required');

        const invitationToken = crypto.randomUUID ? crypto.randomUUID() : `inv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        const now = new Date().toISOString();

        await addDoc(collection(this.db, 'user_invitations'), {
            email: emailLower,
            name: name || '',
            companyId: tenantId,
            role: roleId,
            roleId,
            invitationToken,
            status: 'pending',
            createdAt: now,
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        });

        return { email: emailLower };
    }

    /**
     * Self-service tenant admin claim. Assigns the current Firebase Auth
     * user the tenant `admin` role via the assignUserRole callable.
     *
     * CHA-97 / PR-C (Decision D-5): the legacy "preserve platform_*"
     * logic was dropped — it only compensated for the now-removed
     * Block 2 backstop in App.jsx.
     *
     * Known limitation: a caller with an existing global platform role
     * (platform_owner / platform_admin) will have that role moved to
     * `history[]` on their roleAssignments doc, since setRoleAssignment
     * overwrites the `active` block. CHA-120 tracks the planned UX guard.
     */
    async claimAdmin(tenantId) {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Sign-in required');

        const fn = httpsCallable(this.fns, 'assignUserRole');
        const result = await fn({
            targetUid: user.uid,
            tenantId,
            roleId: 'admin',
        });
        return result.data;
    }

    async seedBuiltIns(tenantId, overwrite = false) {
        const fn = httpsCallable(this.fns, 'seedTenantRoles');
        const res = await fn({ tenantId, overwrite });
        return res.data;
    }

    async loadPickerOptions(tenantId) {
        const fn = httpsCallable(this.fns, 'getScopePickerOptions');
        const res = await fn({ tenantId });
        return res.data?.options || {};
    }
}

const service = new RolesUsersService();

// ------------------------------------------------------------------------
// Utility helpers
// ------------------------------------------------------------------------
const fmtMoney = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
};
const fmtInt = (n) => (Number(n) || 0).toLocaleString('en-US');
const scopeLabel = (values = [ALL]) => {
    if (!values || values.includes(ALL)) return 'All';
    if (values.length === 0) return 'None';
    if (values.length <= 2) return values.join(', ');
    return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
};
const emptyScope = () => ({
    companies: [ALL], divisions: [ALL], customers: [ALL],
    salespersonCodes: [ALL], productionManagerCodes: [ALL], warehouses: [ALL],
    canSeeCosts: false, canSeeMargins: false, canExport: true,
});

// ------------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------------
// CHA-589 — no tenant default: an unresolved tenant renders an error and never reads or writes.
export default function RolesAndUsersPage({ tenantId }) {
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [pickerOptions, setPickerOptions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingRole, setEditingRole] = useState(null); // role object or 'new'
    const [seeding, setSeeding] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [claimingAdmin, setClaimingAdmin] = useState(false);

    const reload = useCallback(async () => {
        // Stay "loading": the error screen covers it, and a later real tenant never paints stale rows.
        if (!tenantId) { setLoading(true); return; }
        setLoading(true); setError(null);
        try {
            const [rs, us] = await Promise.all([
                service.loadRoles(tenantId),
                service.loadUsers(tenantId),
            ]);
            setRoles(rs);
            setUsers(us);
            // Pickers are separate + tolerant: admin UI still renders if they fail
            service.loadPickerOptions(tenantId)
                .then(opts => setPickerOptions(opts))
                .catch(err => console.warn('Picker options failed:', err.message));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    useEffect(() => { reload(); }, [reload]);

    const userCountByRole = useMemo(() => {
        // CHA-97 / PR-C: users come from listUsers callable with canonical
        // `roleId` field. Legacy `role` fallback dropped.
        const m = {};
        users.forEach(u => {
            const rid = u.roleId;
            if (!rid) return;
            m[rid] = (m[rid] || 0) + 1;
        });
        return m;
    }, [users]);

    const handleSeed = async () => {
        setSeeding(true);
        try {
            await service.seedBuiltIns(tenantId, false);
            await reload();
        } catch (e) {
            setError(`Seed failed: ${e.message}`);
        } finally {
            setSeeding(false);
        }
    };

    const handleSaveRole = async (roleId, data) => {
        await service.saveRole(tenantId, roleId, data);
        setEditingRole(null);
        await reload();
    };

    const handleDeleteRole = async (roleId) => {
        if (!window.confirm(`Delete role "${roleId}"? Users with this role will lose access until reassigned.`)) return;
        await service.deleteRole(tenantId, roleId);
        await reload();
    };

    const handleInviteUser = async ({ email, name, roleId }) => {
        await service.inviteUser(tenantId, { email, name, roleId });
        setInviteOpen(false);
        await reload();
    };

    const handleClaimAdmin = async () => {
        setClaimingAdmin(true); setError(null);
        try {
            await service.claimAdmin(tenantId);
            await reload();
        } catch (e) {
            setError(`Claim admin failed: ${e.message}`);
        } finally {
            setClaimingAdmin(false);
        }
    };

    const handleCloneRole = (role) => {
        setEditingRole({
            ...role,
            id: `${role.id}-copy`,
            name: `${role.name} (copy)`,
            isBuiltIn: false,
        });
    };

    if (!tenantId) {
        return (
            <div role="alert" className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">No tenant resolved — Roles & Users cannot load.</div>
            </div>
        );
    }

    if (loading) {
        return <div className="flex items-center justify-center h-64 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading roles & users...
        </div>;
    }

    return (
        <div className="space-y-6">
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">{error}</div>
                    <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800"><X className="h-4 w-4" /></button>
                </div>
            )}

            {/* Roles section */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                        <Shield className="h-5 w-5 text-slate-600" />
                        <h2 className="text-lg font-semibold text-slate-800">Roles</h2>
                        <span className="text-sm text-slate-500">({roles.length})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        {roles.length === 0 && (
                            <button onClick={handleSeed} disabled={seeding}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                <span>Seed built-in roles</span>
                            </button>
                        )}
                        <button onClick={() => setEditingRole({ id: '', name: '', description: '', isBuiltIn: false, pages: [], dataScope: emptyScope(), actions: ['read'] })}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900">
                            <Plus className="h-4 w-4" />
                            <span>New role</span>
                        </button>
                    </div>
                </div>

                {roles.length === 0 ? (
                    <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-500">
                        No roles yet. Click <strong>Seed built-in roles</strong> to create Admin, Executive, Sales, and Production — then edit any to customize scope.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {roles.map(role => (
                            <RoleCard key={role.id}
                                role={role}
                                userCount={userCountByRole[role.id] || 0}
                                onEdit={() => setEditingRole(role)}
                                onClone={() => handleCloneRole(role)}
                                onDelete={role.isBuiltIn ? null : () => handleDeleteRole(role.id)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Users section */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                        <UsersIcon className="h-5 w-5 text-slate-600" />
                        <h2 className="text-lg font-semibold text-slate-800">Users</h2>
                        <span className="text-sm text-slate-500">({users.length})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                        {users.length === 0 && roles.some(r => r.id === 'admin') && (
                            <button onClick={handleClaimAdmin} disabled={claimingAdmin}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
                                {claimingAdmin ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                                <span>Claim me as Admin</span>
                            </button>
                        )}
                        <button onClick={() => setInviteOpen(true)}
                            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900">
                            <Plus className="h-4 w-4" />
                            <span>Invite user</span>
                        </button>
                    </div>
                </div>
                <UsersTable users={users} roles={roles} onUpdate={async (uid, updates) => {
                    // CHA-97 / PR-C: dispatch on update shape.
                    //   { roleId } → assignUserRole callable via service.updateUser
                    //   { status } → setUserStatus callable via service.setUserStatus
                    // (UsersTable always emits exactly one key per change.)
                    if (updates.roleId !== undefined) {
                        await service.updateUser(uid, tenantId, updates);
                    } else if (updates.status !== undefined) {
                        await service.setUserStatus(uid, tenantId, updates.status);
                    } else {
                        console.warn('UsersTable.onUpdate: unrecognized update shape', updates);
                    }
                    await reload();
                }} />
            </section>

            {inviteOpen && (
                <InviteUserModal
                    roles={roles}
                    onClose={() => setInviteOpen(false)}
                    onInvite={handleInviteUser}
                />
            )}

            {/* Role editor modal */}
            {editingRole && (
                <RoleEditor
                    tenantId={tenantId}
                    initialRole={editingRole}
                    pickerOptions={pickerOptions}
                    onClose={() => setEditingRole(null)}
                    onSave={handleSaveRole}
                />
            )}
        </div>
    );
}

// ------------------------------------------------------------------------
// Role Card — scannable at a glance
// ------------------------------------------------------------------------
function RoleCard({ role, userCount, onEdit, onClone, onDelete }) {
    const ds = role.dataScope || {};
    const chips = [
        { label: scopeLabel(ds.companies), color: 'blue' },
        { label: `${ds.divisions && !ds.divisions.includes(ALL) ? ds.divisions.length : 'all'} divs`, color: 'purple' },
        { label: `${ds.customers && !ds.customers.includes(ALL) ? ds.customers.length : 'all'} cust`, color: 'emerald' },
    ];
    const flagChips = [];
    if (ds.canSeeCosts) flagChips.push({ icon: DollarSign, label: 'costs', color: 'green' });
    else flagChips.push({ icon: EyeOff, label: 'no costs', color: 'slate' });
    if (ds.canExport) flagChips.push({ icon: Download, label: 'export', color: 'blue' });

    const colorCls = (c) => ({
        blue: 'bg-blue-50 text-blue-700 border-blue-200',
        purple: 'bg-purple-50 text-purple-700 border-purple-200',
        emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        green: 'bg-green-50 text-green-700 border-green-200',
        slate: 'bg-slate-100 text-slate-600 border-slate-200',
    }[c] || 'bg-slate-100 text-slate-600 border-slate-200');

    return (
        <div className="bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all">
            <div className="p-4 border-b border-slate-100">
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                            <h3 className="text-base font-semibold text-slate-900 truncate">{role.name || role.id}</h3>
                            {role.isBuiltIn && (
                                <span className="inline-flex items-center text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                                    <Lock className="h-2.5 w-2.5 mr-0.5" /> built-in
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{role.description || '—'}</p>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                        <span className="inline-flex items-center text-xs font-medium text-slate-700 bg-slate-100 rounded-full px-2 py-0.5">
                            {userCount} user{userCount === 1 ? '' : 's'}
                        </span>
                    </div>
                </div>
            </div>
            <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-1.5">
                    {chips.map((c, i) => (
                        <span key={i} className={`text-xs font-medium rounded border px-2 py-0.5 ${colorCls(c.color)}`}>
                            {c.label}
                        </span>
                    ))}
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {flagChips.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <span key={i} className={`inline-flex items-center text-xs rounded border px-2 py-0.5 ${colorCls(c.color)}`}>
                                <Icon className="h-3 w-3 mr-1" /> {c.label}
                            </span>
                        );
                    })}
                </div>
                <div className="text-xs text-slate-500 border-t border-slate-100 pt-2">
                    {role.pages?.includes('*') ? 'All pages' : `${role.pages?.length || 0} pages`}
                </div>
            </div>
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 rounded-b-lg flex items-center justify-between">
                <button onClick={onEdit} className="flex items-center space-x-1 text-sm font-medium text-slate-700 hover:text-blue-600">
                    <Edit3 className="h-3.5 w-3.5" /> <span>Edit</span>
                </button>
                <div className="flex items-center space-x-2">
                    <button onClick={onClone} className="text-slate-500 hover:text-slate-800" title="Clone">
                        <Copy className="h-3.5 w-3.5" />
                    </button>
                    {onDelete && (
                        <button onClick={onDelete} className="text-slate-500 hover:text-red-600" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------
// Users Table
// ------------------------------------------------------------------------
function UsersTable({ users, roles, onUpdate }) {
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return users;
        // CHA-97 / PR-C: shape is now { uid, email, displayName, disabled,
        // roleId, ... } from the listUsers callable. Search by email +
        // displayName (with email fallback for users whose Firebase Auth
        // record has no displayName).
        return users.filter(u =>
            (u.email || '').toLowerCase().includes(q) ||
            (u.displayName || u.email || '').toLowerCase().includes(q)
        );
    }, [users, search]);

    const roleOptions = useMemo(() => roles.map(r => ({ value: r.id, label: r.name || r.id })), [roles]);

    return (
        <div className="bg-white rounded-lg border border-slate-200">
            <div className="p-3 border-b border-slate-100 flex items-center">
                <Search className="h-4 w-4 text-slate-400 mr-2" />
                <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-1 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent"
                />
                <span className="text-xs text-slate-500">{filtered.length} shown</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 tracking-wider">
                        <tr>
                            <th className="px-4 py-2 font-medium">User</th>
                            <th className="px-4 py-2 font-medium">Email</th>
                            <th className="px-4 py-2 font-medium">Role</th>
                            <th className="px-4 py-2 font-medium">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filtered.length === 0 && (
                            <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500 italic">No users found.</td></tr>
                        )}
                        {filtered.map(u => (
                            // Orphan row (roleAssignment exists but no Firebase Auth user) — see CHA-118.
                            // Surface with reduced opacity + italics label so admin can spot it for cleanup.
                            <tr key={u.uid} className={`hover:bg-slate-50 ${u._orphan ? 'opacity-60' : ''}`}>
                                <td className="px-4 py-2 font-medium text-slate-800">
                                    {u.displayName || u.email || '—'}
                                    {u._orphan && <span className="ml-2 text-xs italic text-amber-700">(orphan: no auth user)</span>}
                                </td>
                                <td className="px-4 py-2 text-slate-600">{u.email || '—'}</td>
                                <td className="px-4 py-2">
                                    <select
                                        value={u.roleId || ''}
                                        onChange={e => onUpdate(u.uid, { roleId: e.target.value })}
                                        className="text-sm border border-slate-200 rounded px-2 py-1 bg-white hover:border-slate-400"
                                    >
                                        <option value="">— no role —</option>
                                        {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-2">
                                    {/* CHA-97 / PR-C: status is now derived from Firebase Auth's `disabled`
                                        boolean. Only 'active' and 'inactive' are valid; pending users live
                                        in user_invitations (separate UI surface). */}
                                    <select
                                        value={u.disabled ? 'inactive' : 'active'}
                                        onChange={e => onUpdate(u.uid, { status: e.target.value })}
                                        disabled={u._orphan}
                                        className={`text-xs font-medium rounded-full px-2 py-0.5 border-0 ${
                                            u.disabled ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                        }`}
                                    >
                                        <option value="active">active</option>
                                        <option value="inactive">inactive</option>
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------
// Role Editor Modal — name, pages, data scope, capability flags
// ------------------------------------------------------------------------
function RoleEditor({ tenantId, initialRole, pickerOptions, onClose, onSave }) {
    const [form, setForm] = useState(() => ({
        id: initialRole.id || '',
        name: initialRole.name || '',
        description: initialRole.description || '',
        isBuiltIn: !!initialRole.isBuiltIn,
        pages: initialRole.pages?.includes('*') ? 'ALL' : [...(initialRole.pages || [])],
        dataScope: { ...emptyScope(), ...(initialRole.dataScope || {}) },
        actions: initialRole.actions || ['read'],
    }));
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const isNew = !initialRole.name || !roles_include(initialRole);
    function roles_include() { return initialRole.createdAt; }

    const setPages = (v) => setForm(f => ({ ...f, pages: v }));
    const toggleAllPages = () => setPages(form.pages === 'ALL' ? [] : 'ALL');
    const setScope = (dim, values) => setForm(f => ({ ...f, dataScope: { ...f.dataScope, [dim]: values } }));
    const setFlag = (flag, value) => setForm(f => ({ ...f, dataScope: { ...f.dataScope, [flag]: value } }));

    const togglePage = (pageId) => {
        if (form.pages === 'ALL') {
            // Dropping "all" back to explicit list
            const allIds = PAGE_GROUPS.flatMap(g => g.pages.map(([id]) => id)).filter(id => id !== pageId);
            setPages(allIds);
            return;
        }
        setPages(form.pages.includes(pageId)
            ? form.pages.filter(p => p !== pageId)
            : [...form.pages, pageId]);
    };

    const handleSave = async () => {
        if (!form.id || !form.name) { setErr('ID and name are required.'); return; }
        setSaving(true); setErr(null);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                isBuiltIn: form.isBuiltIn,
                pages: form.pages === 'ALL' ? ['*'] : form.pages,
                dataScope: form.dataScope,
                actions: form.actions,
            };
            await onSave(form.id, payload);
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                            <Shield className="h-5 w-5 mr-2 text-blue-600" />
                            {initialRole.createdAt ? 'Edit role' : 'New role'}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">Changes take effect immediately for existing users.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {err && (
                        <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">{err}</div>
                    )}

                    {/* Identity */}
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Role ID <span className="text-red-500">*</span></label>
                            <input type="text"
                                value={form.id}
                                onChange={e => setForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-') }))}
                                disabled={!!initialRole.createdAt}
                                placeholder="sales-walmart"
                                className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-500" />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Display name <span className="text-red-500">*</span></label>
                            <input type="text"
                                value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="Sales — Walmart"
                                className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                        <input type="text"
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="Who has this role and why"
                            className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500" />
                    </div>

                    {/* Pages */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold text-slate-800">Page access</label>
                            <label className="flex items-center text-xs text-slate-600 cursor-pointer">
                                <input type="checkbox"
                                    checked={form.pages === 'ALL'}
                                    onChange={toggleAllPages}
                                    className="mr-1.5" />
                                All pages (current + future)
                            </label>
                        </div>
                        <div className={`border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-x-6 gap-y-1 max-h-64 overflow-y-auto ${form.pages === 'ALL' ? 'opacity-50 pointer-events-none' : ''}`}>
                            {PAGE_GROUPS.map(group => (
                                <div key={group.label} className="space-y-1">
                                    <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-1">{group.label}</div>
                                    {group.pages.map(([pageId, label]) => (
                                        <label key={pageId} className="flex items-center text-sm text-slate-700 cursor-pointer hover:text-slate-900">
                                            <input type="checkbox"
                                                checked={form.pages === 'ALL' || form.pages.includes(pageId)}
                                                onChange={() => togglePage(pageId)}
                                                className="mr-2" />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Data scope */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-semibold text-slate-800">Data scope</label>
                            {!pickerOptions && <span className="text-xs text-amber-600">Loading options from BigQuery...</span>}
                        </div>
                        <div className="space-y-3">
                            {ALL_DIMENSIONS.map(dim => (
                                <ScopePicker key={dim.key}
                                    dim={dim}
                                    selected={form.dataScope[dim.key] || [ALL]}
                                    options={pickerOptions?.[dim.key] || []}
                                    onChange={values => setScope(dim.key, values)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Capability flags */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-800 mb-2">Capabilities</label>
                        <div className="space-y-2">
                            {CAPABILITY_FLAGS.map(flag => (
                                <label key={flag.key} className="flex items-start p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                                    <input type="checkbox"
                                        checked={!!form.dataScope[flag.key]}
                                        onChange={e => setFlag(flag.key, e.target.checked)}
                                        className="mt-0.5 mr-3" />
                                    <div>
                                        <div className="text-sm font-medium text-slate-800">{flag.label}</div>
                                        <div className="text-xs text-slate-500">{flag.description}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-end space-x-2">
                    <button onClick={onClose} className="px-4 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="flex items-center space-x-1 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        <span>Save role</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------
// ScopePicker — one row per dimension, expandable options list with search
// ------------------------------------------------------------------------
function ScopePicker({ dim, selected, options, onChange }) {
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');
    const isAll = selected.includes(ALL);
    const selectedSet = new Set(selected);

    const filtered = useMemo(() => {
        if (!search) return options;
        const q = search.toLowerCase();
        return options.filter(o => (o.label || '').toLowerCase().includes(q) || (o.code || '').toLowerCase().includes(q));
    }, [options, search]);

    const toggleAll = () => onChange(isAll ? [] : [ALL]);
    const toggleOne = (code) => {
        if (isAll) { onChange([code]); return; }
        const next = selectedSet.has(code) ? selected.filter(c => c !== code) : [...selected, code];
        onChange(next.length === 0 ? [] : next);
    };

    const summary = isAll
        ? `All (${options.length || '?'} options)`
        : selected.length === 0 ? 'None (no access)' : `${selected.length} selected`;

    return (
        <div className="border border-slate-200 rounded-lg">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50"
                type="button"
            >
                <div className="text-left">
                    <div className="text-sm font-medium text-slate-800">{dim.label}</div>
                    <div className="text-xs text-slate-500">{summary}</div>
                </div>
                <div className="flex items-center space-x-3">
                    <label className="flex items-center text-xs text-slate-600 cursor-pointer" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isAll} onChange={toggleAll} className="mr-1" />
                        All
                    </label>
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
            </button>
            {expanded && (
                <div className="border-t border-slate-200 p-3 space-y-2">
                    {options.length > 8 && (
                        <div className="flex items-center bg-slate-50 rounded px-2 py-1">
                            <Search className="h-3.5 w-3.5 text-slate-400 mr-2" />
                            <input type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder={`Search ${dim.label.toLowerCase()}...`}
                                className="flex-1 text-xs bg-transparent border-0 focus:outline-none focus:ring-0" />
                        </div>
                    )}
                    {options.length === 0 ? (
                        <div className="text-xs text-slate-500 italic p-2">
                            No options loaded. Type codes directly:
                            <input type="text"
                                defaultValue={isAll ? '' : selected.join(', ')}
                                placeholder="CODE1, CODE2"
                                onBlur={e => {
                                    const codes = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                                    onChange(codes);
                                }}
                                className="mt-1 w-full text-xs border border-slate-200 rounded px-2 py-1" />
                        </div>
                    ) : (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                            {filtered.map(opt => {
                                const checked = isAll || selectedSet.has(opt.code);
                                return (
                                    <label key={opt.code}
                                        className={`flex items-center justify-between text-sm px-2 py-1 rounded cursor-pointer ${
                                            checked ? 'bg-blue-50' : 'hover:bg-slate-50'
                                        }`}>
                                        <div className="flex items-center flex-1 min-w-0">
                                            <input type="checkbox" checked={checked} onChange={() => toggleOne(opt.code)} className="mr-2" />
                                            <span className="truncate font-medium text-slate-800">{opt.label}</span>
                                            {opt.label !== opt.code && <span className="ml-2 text-xs text-slate-400">{opt.code}</span>}
                                        </div>
                                        <div className="ml-2 text-xs text-slate-400 flex-shrink-0 tabular-nums">
                                            {fmtInt(opt.count)}{opt.amount ? ` · ${fmtMoney(opt.amount)}` : ''}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ------------------------------------------------------------------------
// Invite User Modal
// ------------------------------------------------------------------------
function InviteUserModal({ roles, onClose, onInvite }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [roleId, setRoleId] = useState(roles[0]?.id || '');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    const selectedRole = roles.find(r => r.id === roleId);

    const handleSubmit = async () => {
        if (!email.trim()) { setErr('Email is required'); return; }
        if (!roleId) { setErr('Pick a role'); return; }
        setSubmitting(true); setErr(null);
        try {
            await onInvite({ email: email.trim(), name: name.trim(), roleId });
        } catch (e) {
            setErr(e.message || String(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                        <UsersIcon className="h-5 w-5 mr-2 text-blue-600" />
                        Invite user
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
                </div>

                <div className="p-6 space-y-4">
                    {err && <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">{err}</div>}

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email <span className="text-red-500">*</span></label>
                        <input type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder="user@inmocean.com"
                            autoFocus
                            className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500" />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Full name</label>
                        <input type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Jane Doe"
                            className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500" />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Role <span className="text-red-500">*</span></label>
                        <select value={roleId}
                            onChange={e => setRoleId(e.target.value)}
                            className="w-full text-sm border border-slate-200 rounded px-3 py-1.5 focus:ring-2 focus:ring-blue-500 bg-white">
                            {roles.map(r => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
                        </select>
                        {selectedRole && (
                            <div className="mt-1 text-xs text-slate-500">{selectedRole.description || '—'}</div>
                        )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                        An invitation email will be sent with a secure link. The user creates their password on first login.
                    </div>
                </div>

                <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-end space-x-2">
                    <button onClick={onClose} className="px-4 py-1.5 text-sm font-medium text-slate-700 hover:text-slate-900">Cancel</button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="flex items-center space-x-1 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                        <span>Send invite</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
